import type { Locale } from './locales';

/**
 * Locale-aware formatters.
 *
 * Arabic uses Western Arabic numerals (`latn`) deliberately: Eastern Arabic
 * numerals are correct in prose but read poorly in mixed technical content such
 * as version numbers and project years, and they break visual alignment in
 * tables. Revisit if the content team asks for it — the decision lives here and
 * nowhere else.
 */
const NUMBERING_SYSTEM: Readonly<Record<Locale, string>> = {
  en: 'latn',
  ar: 'latn',
};

function tag(locale: Locale): string {
  return `${locale}-u-nu-${NUMBERING_SYSTEM[locale]}`;
}

export function formatNumber(
  locale: Locale,
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(tag(locale), options).format(value);
}

export function formatDate(
  locale: Locale,
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' },
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(tag(locale), { calendar: 'gregory', ...options }).format(date);
}

export function formatYear(locale: Locale, year: number): string {
  return formatNumber(locale, year, { useGrouping: false });
}

export function formatList(locale: Locale, items: readonly string[]): string {
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
}
